import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { buildActivityCalendar, formatUtcDate } from './activityCalendar';

interface ContributionCalendarProps {
  year: number;
  days: ReadonlyArray<{ date: string; count: number }>;
  total: number;
  loading: boolean;
}

const levelFor = (count: number) => (count === 0 ? 0 : count >= 4 ? 4 : count);

export default function ContributionCalendar({
  year,
  days,
  total,
  loading,
}: ContributionCalendarProps) {
  const theme = useTheme();
  const { weeks, months } = buildActivityCalendar(year, days);
  const colors = [
    theme.palette.studio.surfaces.trackSoft,
    alpha(theme.palette.success.main, 0.25),
    alpha(theme.palette.success.main, 0.48),
    alpha(theme.palette.success.main, 0.72),
    theme.palette.success.main,
  ];

  return (
    <Paper variant="panel" sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={0.5}
        mb={2}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {loading ? (
            <Skeleton width={180} />
          ) : (
            `${total} contributions in ${year}`
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Each square is a UTC day you published a version
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '30px minmax(0, 1fr)',
          gap: 1,
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            pt: '19px',
            display: 'grid',
            gridTemplateRows: 'repeat(7, 1fr)',
            gap: '3px',
            color: 'text.secondary',
            fontSize: 10,
          }}
        >
          {['Sun', '', 'Tue', '', 'Thu', '', 'Sat'].map((day, index) => (
            <span key={`${day}-${index}`}>{day}</span>
          ))}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              height: 16,
              display: 'grid',
              gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))`,
              color: 'text.secondary',
              fontSize: 10,
            }}
          >
            {months.map((month) => (
              <Box
                key={month.label}
                component="span"
                sx={{
                  gridColumnStart: month.weekIndex + 1,
                  whiteSpace: 'nowrap',
                  display: {
                    xs: month.weekIndex % 2 ? 'none' : 'block',
                    sm: 'block',
                  },
                }}
              >
                {month.label}
              </Box>
            ))}
          </Box>
          <Box
            role="grid"
            aria-label={`Daily project-version contributions for ${year}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${weeks.length}, minmax(3px, 1fr))`,
              gap: { xs: '2px', sm: '3px' },
            }}
          >
            {weeks.map((week, weekIndex) => (
              <Box
                role="row"
                key={weekIndex}
                sx={{
                  display: 'grid',
                  gridTemplateRows: 'repeat(7, 1fr)',
                  gap: { xs: '2px', sm: '3px' },
                }}
              >
                {week.map((day) => {
                  const label = `${day.count} ${day.count === 1 ? 'version' : 'versions'} on ${formatUtcDate(day.date)}`;
                  const cell = (
                    <Box
                      component={day.count > 0 ? 'button' : 'span'}
                      type={day.count > 0 ? 'button' : undefined}
                      role="gridcell"
                      aria-label={day.count > 0 ? label : undefined}
                      key={day.key}
                      sx={{
                        display: 'block',
                        width: '100%',
                        aspectRatio: '1',
                        minWidth: 0,
                        p: 0,
                        border: 0,
                        borderRadius: '2px',
                        bgcolor: day.inYear
                          ? colors[levelFor(day.count)]
                          : 'transparent',
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '1px',
                        },
                      }}
                    />
                  );
                  return day.count > 0 ? (
                    <Tooltip title={label} arrow key={day.key}>
                      {cell}
                    </Tooltip>
                  ) : (
                    cell
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={0.5}
        mt={1.5}
      >
        <Typography variant="caption" color="text.secondary">
          Less
        </Typography>
        {colors.map((color, index) => (
          <Box
            key={index}
            aria-hidden="true"
            sx={{ width: 11, height: 11, borderRadius: '2px', bgcolor: color }}
          />
        ))}
        <Typography variant="caption" color="text.secondary">
          More
        </Typography>
      </Stack>
    </Paper>
  );
}
